import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Collection Email Drafts RPC Contract
 *
 * Procedures for AI-assisted email draft generation against a collection
 * thread. The handler walks the thread's evidence, classifies the situation
 * (e.g. short-pay outcome category), and persists an EmailDraft seeded with
 * an AI-generated body. The dialog then edits via the generic emailDrafts.
 * {get, update, send} pipeline.
 *
 * Categories are scoped to short-pays for v1; the union expands as we add
 * other thread types (credit limit expiry, broken promise, etc.).
 */

export const CollectionEmailDraftModeSchema = z.enum(["auto", "manual"]);

export const CollectionEmailClassificationCategorySchema = z.enum([
  "dispute_invalid",
  "accept_short_pay",
  "request_remittance_clarification",
  "request_documentation",
  "escalate",
  "manual_user_directed",
]);

export const CollectionEmailClassificationConfidenceSchema = z.enum([
  "high",
  "medium",
  "low",
  "none",
]);

export const CollectionEmailClassificationSchema = z.object({
  category: CollectionEmailClassificationCategorySchema,
  confidence: CollectionEmailClassificationConfidenceSchema,
  reasoning: z.string(),
  /** True when the model is unsure or detected something requiring human review. */
  needsHumanReview: z.boolean(),
});

/**
 * UPN of the mailbox to record on the persisted EmailDraft. The dialog's
 * Send From selector resolves this client-side from the AR module config
 * (`configured` outbox) or the current user's email (`user_mailbox` mode).
 * Omitted on the auto-fired initial draft, in which case the handler falls
 * back to the AR module's configured outbox. The server authorizes any
 * caller-supplied UPN before persisting it.
 */
const sendingMailboxSchema = z.string().email().optional();

/**
 * Auto-mode runs classification first and uses the chosen category to pick a
 * template/system prompt. An optional `userPrompt` can be passed to steer the
 * draft within that auto path; whitespace-only values are rejected.
 */
const DraftCollectionEmailAutoInputSchema = z.object({
  threadId: z.uuid(),
  mode: z.literal("auto"),
  userPrompt: z.string().trim().min(1).optional(),
  sendingMailbox: sendingMailboxSchema,
});

/**
 * The draft being revised. Sent with a rewrite so the agent revises the
 * existing text instead of starting over. Carries the subject/body as
 * currently shown in the dialog — including any hand edits the user made —
 * which may differ from the persisted draft row.
 */
const CurrentDraftSchema = z.object({
  subject: z.string(),
  htmlBody: z.string(),
});

/**
 * Manual mode skips classification and follows the user prompt directly, so
 * the prompt is required. When `currentDraft` is present the prompt is
 * treated as revision feedback on that draft; when absent it's the intent
 * for a fresh draft.
 */
const DraftCollectionEmailManualInputSchema = z.object({
  threadId: z.uuid(),
  mode: z.literal("manual"),
  userPrompt: z.string().trim().min(1),
  sendingMailbox: sendingMailboxSchema,
  currentDraft: CurrentDraftSchema.optional(),
});

export const DraftCollectionEmailInputSchema = z.discriminatedUnion("mode", [
  DraftCollectionEmailAutoInputSchema,
  DraftCollectionEmailManualInputSchema,
]);

export const DraftCollectionEmailOutputSchema = z.object({
  draftId: z.uuid(),
  /**
   * Null when the request was `manual` (no classification was run).
   * Populated for `auto` runs so the UI can surface category + reasoning.
   */
  classification: CollectionEmailClassificationSchema.nullable(),
});

export const arCollectionEmailDraftsContracts = {
  draft: oc
    .input(DraftCollectionEmailInputSchema)
    .output(DraftCollectionEmailOutputSchema)
    .route({
      method: "POST",
      summary: "Draft a collection email with AI",
      description:
        "Generates and saves an unsent email draft for a collection thread, either auto-classifying the situation or following a user prompt or revision; returns the draft id and classification.",
    }),
} as const;

export type CollectionEmailDraftMode = z.infer<
  typeof CollectionEmailDraftModeSchema
>;
export type CollectionEmailClassificationCategory = z.infer<
  typeof CollectionEmailClassificationCategorySchema
>;
export type CollectionEmailClassificationConfidence = z.infer<
  typeof CollectionEmailClassificationConfidenceSchema
>;
export type CollectionEmailClassification = z.infer<
  typeof CollectionEmailClassificationSchema
>;
export type DraftCollectionEmailInput = z.infer<
  typeof DraftCollectionEmailInputSchema
>;
export type DraftCollectionEmailOutput = z.infer<
  typeof DraftCollectionEmailOutputSchema
>;
